import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as Actions from './actions';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { enqueueNotification } from 'store/modules/ui';
import { otpEmailSelector } from './selectors';
import { RequestOtpResponseMessages } from './model';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { setAccessUuidForItem } from 'containers/Voucher/utils';
import { AxiosResponse } from 'axios';
import { ValidateOtpResponse } from 'services/ItineraryManagerApi/types';

export function* requestOtpEmailSaga(action: Actions.RequestOtpEmailRequestAction) {
  const api = makeItineraryManagerApi();
  const dynamicParameters = ParameterService.getParameters();
  const email: string = yield select(otpEmailSelector);
  const itemUuid: string = action.itemUuid;
  const search = action.history.location.search;

  function* redirectToOtpVerificationPage() {
    yield put(
      enqueueNotification({
        message: 'Code sent successfully.',
        options: { variant: 'success' },
      })
    );
    yield put(Actions.setResendTimerAction(Date.now() + (dynamicParameters.OTP_NEW_REQUEST_TIME_SECONDS || 60) * 1000));
    if (!action.history.location.pathname.includes('/otp/enter-code')) {
      yield call(action.history.push, `/otp/enter-code/${itemUuid}${search}`);
    }
  }

  try {
    yield call(api.requestOtp, itemUuid, email);
    yield put(Actions.requestOtpEmailSuccessAction(200));
    yield* redirectToOtpVerificationPage()
  } catch (e: any) {
    if (Number(e?.response?.status) === 422) {
      switch (e.response?.data?.errors[0]?.message) {
        case RequestOtpResponseMessages.ALREADY_REQUESTED:
          yield put(Actions.setResendTimerAction(Date.now() + (dynamicParameters.OTP_NEW_REQUEST_TIME_SECONDS || 60) * 1000));
          yield call(action.history.push, `/otp/enter-code/${itemUuid}${search}`);
          yield put(
            enqueueNotification({
              message: e.response?.data?.errors[0]?.message,
              options: { variant: 'success' },
            })
          );
          yield put(Actions.requestOtpEmailSuccessAction(e.response?.status));
          break;
        case RequestOtpResponseMessages.EMAIL_NOT_FOUND:
          yield* redirectToOtpVerificationPage();
          yield put(Actions.requestOtpEmailSuccessAction(e.response?.status));
          break;
      }
      return;
    }
    yield put(Actions.requestOtpEmailFailureAction(e.response?.data?.errors));
    yield put(
      enqueueNotification({
        message: e.response?.data?.errors[0].message || 'Something went wrong.',
        options: { variant: 'error' },
      })
    );
  }
}

export function* verifyOtpEmailSaga(action: Actions.VerifyOtpEmailRequestAction) {
  const api = makeItineraryManagerApi();
  const itemUuid: string = action.itemUuid;
  const email: string = action.email;
  const otp: string = action.otp;

  try {
    const res: AxiosResponse<ValidateOtpResponse> = yield call(api.validateOtp, itemUuid, email, otp);
    yield put(Actions.verifyOtpEmailSuccessAction(200));
    yield call(setAccessUuidForItem, itemUuid, res.data.accessUuid);
    yield call(action.history.push, new URLSearchParams(window.location.search).get("redirect"));
    yield put(Actions.resetOtpSubdomainAction());
  } catch (e: any) {
    yield put(Actions.verifyOtpEmailFailureAction(e.response?.data?.errors.map(er => er.message)));
    yield put(
      enqueueNotification({
        message: e.response?.data?.errors[0].message || 'Something went wrong.',
        options: { variant: 'error' },
      })
    );
  }
}

export function* watchOtpSaga() {
  yield takeLatest(Actions.REQUEST_OTP_EMAIL_REQUEST, requestOtpEmailSaga);
  yield takeLatest(Actions.VERIFY_OTP_EMAIL_REQUEST, verifyOtpEmailSaga);
}